import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {DashboardWidget} from 'app/components/Dashboard/DashboardWidget';
import {useLensWidgetShareDialog} from 'app/components/Dashboard/hooks/useLensWidgetShareDialog';
import {useMultiFeatureAnalysisData} from 'app/components/Dashboard/hooks/useMultiFeatureAnalysisData';
import {ChartDescription} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/ChartDescription';
import MultiFeatureAnalysisLineChart from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisChart';
import {MultiFeatureAnalysisSkeleton} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisSkeleton';
import MultiFeatureAnalysisTable, {
  FeatureStatsRowData,
} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisTable';
import {MFAWidgetControls} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/WidgetControls';
import {MFAResponse, MultifeatureAnalysisConfig} from 'app/components/Dashboard/types';
import {
  MAX_SELECTABLE_FEATURES,
  calculateTrendLine,
  getAllSortedTimestamps,
  getSumMeanLegendItems,
  getTrendLineAggregation,
  handleDownloadPng,
  useMFADataFilters,
} from 'app/components/Dashboard/utils/chartUtils';
import {
  formatMultiFeatureTableRows,
  handleDownloadCsv,
} from 'app/components/Dashboard/utils/tableUtils';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganization, TagSetting} from 'app/modules/Remote/Organization';
import {getLayer} from 'app/utils/layerUtils';

interface MultiFeatureAnalysisWidgetProps {
  widgetConfig: MultifeatureAnalysisConfig;
  organization: I.ImmutableOf<ApiOrganization>;
}

type MultiFeatureAnalysisViewProps = {
  widgetConfig: MultifeatureAnalysisConfig;
  data?: MFAResponse;
  isLoading: boolean;
  error?: {message?: string} | null;
  noDataFound?: boolean;
  orgTags?: TagSetting[];
  layer?: ReturnType<typeof getLayer>;
  allDates?: string[];
  shareFn?: () => void;
  organization?: I.ImmutableOf<ApiOrganization>;
};

export const MultiFeatureAnalysisWidget: React.FC<MultiFeatureAnalysisWidgetProps> = ({
  widgetConfig,
  organization,
}) => {
  const {data, isLoading, error, layer, allDates, noDataFound} = useMultiFeatureAnalysisData(
    organization.get('id'),
    widgetConfig
  );

  const orgTags = React.useMemo<TagSetting[]>(() => {
    return organization.getIn(['settings', 'feature_tags'])?.toJS() || [];
  }, [organization]);

  const {openModal: openShareModal, dialog: shareDialog} = useLensWidgetShareDialog(widgetConfig);

  return (
    <>
      <MultiFeatureAnalysisView
        widgetConfig={widgetConfig}
        data={data as MFAResponse} // Comes in a FeatureStatsApiResponse
        isLoading={isLoading}
        error={error}
        noDataFound={noDataFound}
        orgTags={orgTags}
        layer={layer}
        allDates={allDates}
        shareFn={openShareModal}
        organization={organization}
      />
      {shareDialog}
    </>
  );
};

export const MultiFeatureAnalysisView: React.FC<MultiFeatureAnalysisViewProps> = ({
  widgetConfig,
  data,
  isLoading,
  error,
  noDataFound,
  orgTags = [],
  layer: providedLayer,
  allDates: providedAllDates,
  shareFn,
  organization,
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const pushNotification = usePushNotification();

  const chartHidden = widgetConfig.displayOptions?.chartHidden;
  const [isExpanded, setIsExpanded] = React.useState(chartHidden || !shareFn);
  const [hoveredFeatureId, setHoveredFeatureId] = React.useState<string | null>(null);
  const [displayFeatureIds, setDisplayFeatureIds] = React.useState<Set<string>>(
    new Set(['trendline'])
  );

  const layer = React.useMemo(
    () => providedLayer ?? getLayer(widgetConfig.layerKey),
    [providedLayer, widgetConfig.layerKey]
  );

  const allDates = React.useMemo(
    () => providedAllDates ?? getAllSortedTimestamps(data) ?? [],
    [providedAllDates, data]
  );

  const showAggregateTrend = displayFeatureIds.has('trendline');

  const trendLineAggregation = getTrendLineAggregation(widgetConfig);

  const {filteredData, filterStates, filterActions} = useMFADataFilters(data, orgTags);

  const tableRows = React.useMemo(() => {
    if (!filteredData) return [];
    return formatMultiFeatureTableRows(filteredData);
  }, [filteredData]);

  const trendLine = React.useMemo(() => {
    if (!filteredData) return {};
    return calculateTrendLine(filteredData, trendLineAggregation);
  }, [filteredData, trendLineAggregation]);

  const handleExportPng = React.useCallback(() => {
    if (!filteredData) return;

    handleDownloadPng(
      chartRef.current?.querySelector('canvas'),
      widgetConfig,
      layer,
      isExpanded,
      () => {
        pushNotification({
          message: 'Error downloading chart',
          options: {
            intent: B.Intent.DANGER,
          },
        });
      },
      getSumMeanLegendItems({
        data: filteredData,
        displayFeatureIds,
        layerKey: widgetConfig.layerKey,
        statType: widgetConfig.statAggregation,
      })
    );
  }, [filteredData, widgetConfig, layer, isExpanded, displayFeatureIds, pushNotification]);

  const exportTableAsCsv = React.useCallback(() => {
    const exportRows = [...tableRows];
    exportRows.unshift({
      id: 'trendline',
      name: trendLineAggregation === 'sum' ? 'Total' : 'Average',
      ...trendLine,
    } as FeatureStatsRowData);

    handleDownloadCsv(exportRows, widgetConfig, () => {
      pushNotification({
        message: 'Error exporting table as CSV',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    });
  }, [tableRows, widgetConfig, trendLineAggregation, trendLine, pushNotification]);

  const setDisplayFeatureIdsWithLimit: React.Dispatch<React.SetStateAction<Set<string>>> = (
    input: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => {
    const sendLimitNotification = () =>
      pushNotification({
        message: `A maximum of ${MAX_SELECTABLE_FEATURES} properties can be displayed at once`,
        options: {
          intent: B.Intent.WARNING,
        },
      });
    if (typeof input === 'function') {
      setDisplayFeatureIds((prev) => {
        const newFeatureIds = input(prev);
        if (newFeatureIds.size >= MAX_SELECTABLE_FEATURES) {
          sendLimitNotification();
          return prev;
        }
        return newFeatureIds;
      });
    } else {
      if (input.size >= MAX_SELECTABLE_FEATURES) {
        sendLimitNotification();
        return;
      } else {
        setDisplayFeatureIds(input);
      }
    }
  };

  if (isLoading) {
    return <MultiFeatureAnalysisSkeleton size={isExpanded ? 'large' : 'small'} />;
  }

  const hasNoData = !!(error || !filteredData || noDataFound);

  return (
    <DashboardWidget
      title={widgetConfig.title}
      size={isExpanded ? 'large' : 'small'}
      controls={
        shareFn && (
          <MFAWidgetControls
            widgetConfig={widgetConfig}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            onExportPng={handleExportPng}
            onExportCsv={exportTableAsCsv}
            onShare={shareFn}
            chartHidden={chartHidden}
          />
        )
      }
    >
      {hasNoData ? (
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="No data"
          description={
            noDataFound
              ? `No property statistics found for ${layer?.display ?? widgetConfig.layerKey}`
              : (error?.message ?? 'Error visualizing data')
          }
        />
      ) : (
        <>
          <ChartDescription
            widgetConfig={widgetConfig}
            layer={layer}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            sharedView={!shareFn}
          />

          {!chartHidden && (
            <div ref={chartRef}>
              <MultiFeatureAnalysisLineChart
                data={filteredData}
                displayFeatureIds={displayFeatureIds}
                hoveredFeatureId={hoveredFeatureId}
                isExpanded={isExpanded}
                setDisplayFeatureIds={setDisplayFeatureIdsWithLimit}
                widgetConfig={widgetConfig}
                showAggregateTrend={showAggregateTrend}
                trendLineAggregation={trendLineAggregation}
              />
            </div>
          )}

          {(isExpanded || chartHidden) && (
            <MultiFeatureAnalysisTable
              filterStates={filterStates}
              filterActions={filterActions}
              displayFeatureIds={displayFeatureIds}
              setDisplayFeatureIds={setDisplayFeatureIdsWithLimit}
              rows={tableRows}
              allDates={allDates}
              showAggregateTrend={showAggregateTrend}
              widgetConfig={widgetConfig}
              setHoveredFeatureId={setHoveredFeatureId}
              trendLine={trendLine}
              trendLineAggregation={trendLineAggregation}
              organization={organization}
              isExpanded={isExpanded}
            />
          )}
        </>
      )}
    </DashboardWidget>
  );
};
